/**
 * The coach: it observes engine state and explains what to do and why.
 *
 * It never mutates state and never calls a network or model service. Every
 * sentence is derived deterministically from the same heuristics the bot uses,
 * so the advice and the bot can never disagree.
 *
 * The coach is also language-neutral: it returns message descriptors (a key
 * plus typed parameters), and @kikik/i18n renders them in Bahasa Indonesia or
 * English. Advice therefore depends only on the game state, never on the
 * reader's language.
 */

import {
  TILE_INFO,
  getConcealedTiles,
  getLegalActions,
  getPlayer,
  getRuleSet,
  isGameOver,
} from "@kikik/mahjong-core";
import type { Action, GameState, PlayerId, Tile, TileKind } from "@kikik/mahjong-core";
import { planClaim, planDiscards, rankTilesForDiscard, shanten } from "@kikik/mahjong-bot";
import { meldParam, msg, msgParam, playerParam, tileParam, tilesParam } from "@kikik/i18n";
import type { Message } from "@kikik/i18n";

export type AdviceKind =
  | "wait"
  | "draw"
  | "discard"
  | "claim"
  | "pass"
  | "win"
  | "game-over";

/** Notes are labelled so the UI can head them "Pertahankan" / "Keep". */
export type CoachNoteKind = "keep" | "consider";

export interface CoachNote {
  kind: CoachNoteKind;
  message: Message;
}

export interface CoachAdvice {
  kind: AdviceKind;
  /** The action the coach recommends, ready to submit to the engine. */
  action: Action | null;
  /** Short instruction, e.g. "Buang 9 Bambu" / "Discard 9 Bamboo". */
  headline: Message;
  /** Why, in one or two sentences. */
  reason: Message;
  /** Extra pointers, e.g. which tiles to keep. */
  notes: CoachNote[];
  /** Tile the UI should highlight, when the advice is about one tile. */
  tileId: number | null;
}

function keep(message: Message): CoachNote {
  return { kind: "keep", message };
}

function consider(message: Message): CoachNote {
  return { kind: "consider", message };
}

/** The runs a suited tile could still become, given the rest of the hand. */
export function possibleRuns(kind: TileKind, hand: readonly TileKind[]): string[] {
  const info = TILE_INFO[kind];
  if (info.isHonor) return [];
  const ranks = new Set(
    hand
      .filter((other) => TILE_INFO[other].suit === info.suit)
      .map((other) => TILE_INFO[other].rank),
  );

  const runs: string[] = [];
  for (const start of [info.rank - 2, info.rank - 1, info.rank]) {
    if (start < 1 || start + 2 > 9) continue;
    const members = [start, start + 1, start + 2];
    // Only suggest runs the hand has at least one other tile for.
    if (!members.some((rank) => rank !== info.rank && ranks.has(rank))) continue;
    runs.push(members.join("-"));
  }
  return runs;
}

function keepNotes(state: GameState, playerId: PlayerId, exclude: number): CoachNote[] {
  const player = getPlayer(state, playerId);
  const concealed = getConcealedTiles(player).filter((tile) => tile.id !== exclude);
  const kinds = concealed.map((tile) => tile.kind);
  const ranked = rankTilesForDiscard(concealed);
  const best = ranked.at(-1);
  if (!best) return [];

  if (best.reason === "triplet") {
    return [keep(msg("coach.keep.triplet", { tile: tileParam(best.tile.kind) }))];
  }
  if (best.reason === "pair") {
    return [keep(msg("coach.keep.pair", { tile: tileParam(best.tile.kind) }))];
  }
  if (best.connections.length > 0) {
    const runs = possibleRuns(best.tile.kind, kinds);
    const params = {
      tile: tileParam(best.tile.kind),
      others: tilesParam(best.connections),
    };
    return [
      keep(
        runs.length > 0
          ? msg("coach.keep.run", { ...params, runs: runs.join(", ") })
          : msg("coach.keep.run.plain", params),
      ),
    ];
  }
  return [];
}

function discardReason(
  state: GameState,
  playerId: PlayerId,
  tile: Tile,
  reason: string,
): Message {
  const player = getPlayer(state, playerId);
  const kinds = getConcealedTiles(player).map((item) => item.kind);
  const params = { tile: tileParam(tile.kind) };

  switch (reason) {
    case "lone-honor":
      return msg("coach.reason.lone-honor", params);
    case "lone-terminal":
      return msg("coach.reason.lone-terminal", params);
    case "isolated":
      return msg("coach.reason.isolated", params);
    case "run-gap": {
      const runs = possibleRuns(tile.kind, kinds);
      return runs.length > 0
        ? msg("coach.reason.run-gap", { ...params, runs: runs.join(", ") })
        : msg("coach.reason.run-gap.plain", params);
    }
    case "run-part":
      return msg("coach.reason.run-part", params);
    case "pair":
      return msg("coach.reason.pair", params);
    default:
      return msg("coach.reason.default", params);
  }
}

/** How far the hand still is from winning, as a phrase the sentences embed. */
export function stepsMessage(steps: number): Message {
  if (steps <= -1) return msg("coach.steps.complete");
  if (steps === 0) return msg("coach.steps.one");
  return msg("coach.steps.many", { count: steps + 1 });
}

/**
 * What should this player do right now, and why.
 * Returns advice even when it is not the player's turn.
 */
export function getAdvice(state: GameState, playerId: PlayerId): CoachAdvice {
  if (isGameOver(state)) {
    return {
      kind: "game-over",
      action: null,
      headline: msg("coach.gameOver.headline"),
      reason:
        state.result?.type === "win"
          ? msg("coach.gameOver.win", { player: playerParam(state.result.winner) })
          : msg("coach.gameOver.draw"),
      notes: [],
      tileId: null,
    };
  }

  const legal = getLegalActions(state, playerId);
  if (legal.length === 0) {
    return {
      kind: "wait",
      action: null,
      headline: msg("coach.wait.headline"),
      reason: msg("coach.wait.reason"),
      notes: [],
      tileId: null,
    };
  }

  const player = getPlayer(state, playerId);
  const melds = player.melds.length;

  const win = legal.find((action) => action.type === "declare-win");
  if (win) {
    return {
      kind: "win",
      action: win,
      headline: msg("coach.win.headline"),
      reason: msg("coach.win.reason"),
      notes: [consider(msg("coach.win.note"))],
      tileId: null,
    };
  }

  const ron = legal.find((action) => action.type === "ron");
  if (ron) {
    const claimed = state.pendingClaims?.discard.tile;
    return {
      kind: "claim",
      action: ron,
      headline: msg("coach.ron.headline"),
      reason: msg("coach.ron.reason", {
        tile: tileParam(claimed?.kind ?? "CHA1"),
      }),
      notes: [consider(msg("coach.ron.note"))],
      tileId: claimed?.id ?? null,
    };
  }

  if (state.phase === "awaiting-claims") {
    const discard = state.pendingClaims?.discard;
    const claims = legal.filter(
      (action) => action.type === "chow" || action.type === "pong" || action.type === "kong",
    );
    const plans = claims
      .map((action) => planClaim(state, action as never))
      .sort((a, b) => a.shantenAfter - b.shantenAfter);
    const best = plans[0];

    if (best && best.accept) {
      const kind = best.action.type === "ron" ? "pong" : best.action.type;
      return {
        kind: "claim",
        action: best.action,
        headline: msg("coach.claim.headline", { meld: meldParam(kind) }),
        reason: msg("coach.claim.reason", {
          tile: tileParam(discard?.tile.kind ?? "CHA1"),
          before: msgParam(stepsMessage(best.shantenBefore)),
          after: msgParam(stepsMessage(best.shantenAfter)),
        }),
        notes: [consider(msg("coach.claim.note"))],
        tileId: discard?.tile.id ?? null,
      };
    }

    return {
      kind: "pass",
      action: legal.find((action) => action.type === "pass") ?? null,
      headline: msg("coach.pass.headline"),
      reason: best ? msg("coach.pass.reason.noGain") : msg("coach.pass.reason.noUse"),
      notes: [],
      tileId: discard?.tile.id ?? null,
    };
  }

  const draw = legal.find((action) => action.type === "draw");
  if (draw) {
    const steps = shanten(
      getConcealedTiles(player).map((tile) => tile.kind),
      melds,
    );
    return {
      kind: "draw",
      action: draw,
      headline: msg("coach.draw.headline"),
      reason: msg("coach.draw.reason", { steps: msgParam(stepsMessage(steps)) }),
      notes: [consider(msg("coach.draw.note", { count: state.wall.length }))],
      tileId: null,
    };
  }

  const plans = planDiscards(state, playerId);
  const best = plans[0];
  if (!best) {
    return {
      kind: "wait",
      action: null,
      headline: msg("coach.none.headline"),
      reason: msg("coach.none.reason"),
      notes: [],
      tileId: null,
    };
  }

  const waits = getRuleSet(state.ruleSetId).findWaits(
    getConcealedTiles(player)
      .filter((tile) => tile.id !== best.candidate.tile.id)
      .map((tile) => tile.kind),
    melds,
  );

  const notes = keepNotes(state, playerId, best.candidate.tile.id);
  if (waits.length > 0) {
    notes.unshift(consider(msg("coach.note.ready", { tiles: tilesParam(waits) })));
  }

  return {
    kind: "discard",
    action: { type: "discard", playerId, tileId: best.candidate.tile.id },
    headline: msg("coach.discard.headline", { tile: tileParam(best.candidate.tile.kind) }),
    reason: discardReason(state, playerId, best.candidate.tile, best.candidate.reason),
    notes,
    tileId: best.candidate.tile.id,
  };
}

/** A short description of the current situation, for the coach panel header. */
export function describeSituation(state: GameState, playerId: PlayerId): Message {
  if (isGameOver(state)) return msg("coach.situation.ended");
  if (state.phase === "awaiting-claims") {
    const discard = state.pendingClaims?.discard;
    if (!discard) return msg("coach.situation.claimWaiting");
    return msg("coach.situation.claim", {
      player: playerParam(discard.playerId),
      tile: tileParam(discard.tile.kind),
    });
  }
  if (state.turn !== playerId) {
    return msg("coach.situation.otherTurn", { player: playerParam(state.turn) });
  }
  return state.phase === "awaiting-draw"
    ? msg("coach.situation.yourDraw")
    : msg("coach.situation.yourDiscard");
}
